import { useState, useCallback, useRef, useEffect } from 'react';
import { AuthService, LoginRequest } from '../services/AuthService';
import {
  AuthState,
  LoginFormData,
  LoginValidation,
  AuthErrorType,
  AdminInfo
} from '../models/AuthModels';
import { getSimpleErrorMessage } from '../utils/errorMessages';
import TokenManager from '../utils/tokenManager';
import authStateManager from '../utils/authStateManager';

// 인증 ViewModel 훅
export const useAuthViewModel = () => {
  // 인증 상태 관리
  const [authState, setAuthState] = useState<AuthState>({
    isLoggedIn: AuthService.isLoggedIn(),
    isLoading: false,
    adminInfo: AuthService.getAdminInfo(),
    error: null,
  });

  // 로딩 상태 설정
  const setLoading = useCallback((loading: boolean) => {
    setAuthState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  // 에러 상태 설정
  const setError = useCallback((error: string | null) => {
    setAuthState(prev => ({ ...prev, error }));
  }, []);

  // 로그인 성공 처리
  const setLoginSuccess = useCallback((adminInfo: AdminInfo) => {
    console.log('로그인 성공 - 상태 업데이트:', { adminInfo, isLoggedIn: true });
    setAuthState({
      isLoggedIn: true,
      isLoading: false,
      adminInfo,
      error: null,
    });
  }, []);

  // 로그아웃 처리
  const setLogoutState = useCallback(() => {
    setAuthState({
      isLoggedIn: false,
      isLoading: false,
      adminInfo: null,
      error: null,
    });
  }, []);

  // 로그인 폼 유효성 검사
  const validateLoginForm = useCallback((formData: LoginFormData): LoginValidation => {
    const errors: { admin_id?: string; admin_pw?: string } = {};

    // 관리자 ID 검사
    if (!formData.admin_id.trim()) {
      errors.admin_id = '관리자 ID를 입력해주세요.';
    } else if (formData.admin_id.length < 3) {
      errors.admin_id = '관리자 ID는 3자 이상이어야 합니다.';
    }

    // 비밀번호 검사
    if (!formData.admin_pw.trim()) {
      errors.admin_pw = '비밀번호를 입력해주세요.';
    } else if (formData.admin_pw.length < 4) {
      errors.admin_pw = '비밀번호는 4자 이상이어야 합니다.';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }, []);

  // 로그인 처리
  const login = useCallback(async (formData: LoginFormData): Promise<boolean> => {
    console.log('AuthViewModel.login 시작:', formData);

    // 유효성 검사
    const validation = validateLoginForm(formData);
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(' ');
      console.log('유효성 검사 실패:', errorMessages);
      setError(errorMessages);
      return false;
    }

    console.log('유효성 검사 통과, 로딩 시작');
    setLoading(true);
    setError(null);

    try {
      const loginRequest: LoginRequest = {
        admin_id: formData.admin_id,
        admin_pw: formData.admin_pw,
      };

      console.log('AuthService.login 호출 시작:', loginRequest);
      const response = await AuthService.login(loginRequest);
      console.log('AuthService.login 응답 받음:', response);

      if (response.success && response.data) {
        console.log('AuthService.login 성공:', response);

        // 토큰이 실제로 저장되었는지 확인
        const accessToken = TokenManager.getAccessToken();
        const refreshToken = TokenManager.getRefreshToken();
        
        if (!accessToken || !refreshToken) {
          console.error('토큰 저장 확인 실패:', {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken
          });
          setError('토큰 저장에 실패했습니다. 브라우저 설정을 확인해주세요.');
          return false;
        }

        console.log('토큰 저장 확인 완료:', {
          accessToken: accessToken.substring(0, 20) + '...',
          refreshToken: refreshToken.substring(0, 20) + '...'
        });

        // 로그인 성공 - 백엔드 응답의 data 필드를 AdminInfo로 변환
        const adminInfo: AdminInfo = {
          admin_id: response.data.data.admin_id,
          admin_name: response.data.data.admin_name,
          role: 'admin', // 기본값 설정
        };

        console.log('AdminInfo 변환:', adminInfo);
        setLoginSuccess(adminInfo);

        // 로그인 상태 재확인
        const isLoggedInAfter = AuthService.isLoggedIn();
        console.log('로그인 후 상태 재확인:', isLoggedInAfter);

        if (!isLoggedInAfter) {
          console.error('로그인 상태 확인 실패 - 토큰은 저장되었지만 isLoggedIn이 false');
          // 토큰은 저장되었으므로 강제로 상태 업데이트
          setLoginSuccess(adminInfo);
        }

        return true;
      } else {
        // 로그인 실패
        console.log('AuthService.login 실패:', response);
        setError(response.message || '로그인에 실패했습니다.');
        return false;
      }
    } catch (error: any) {
      console.error('로그인 처리 중 오류:', error);
      const errorMessage = getSimpleErrorMessage(error);
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [validateLoginForm, setLoading, setError, setLoginSuccess]);

  // 로그아웃 타이머 참조 (메모리 누수 방지)
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 로그아웃 처리
  const logout = useCallback(() => {
    console.log('AuthViewModel.logout 시작');

    // 기존 타이머 정리
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    // 먼저 상태를 즉시 업데이트하여 UI 반응성 향상
    setLogoutState();

    // AuthService.logout()이 window.location.href로 리다이렉트하므로
    // 이 함수 실행 후 컴포넌트는 언마운트됨
    AuthService.logout();
  }, [setLogoutState]);

  // 토큰 refresh 처리 (AuthStateManager 사용 - 단일 진입점)
  const refreshToken = useCallback(async (): Promise<boolean> => {
    console.log('AuthViewModel.refreshToken 시작 - AuthStateManager 사용');

    try {
      // AuthStateManager를 통한 통합 토큰 갱신 (재시도 로직 포함)
      const newToken = await authStateManager.getValidToken();
      
      if (newToken) {
        console.log('토큰 갱신 성공');
        setError(null); // 성공 시 에러 메시지 클리어
        return true;
      }
      
      return false;
    } catch (error: any) {
      console.error('토큰 refresh 중 오류:', error);

      // 에러 타입에 따른 구체적인 메시지 제공
      let errorMessage = '토큰 갱신 중 오류가 발생했습니다.';
      let shouldLogout = false;

      const errorMsg = error.message || '';
      
      if (errorMsg.includes('Refresh token이 없거나 만료되었습니다') ||
          errorMsg.includes('세션이 만료')) {
        errorMessage = '세션이 만료되었습니다. 다시 로그인해주세요.';
        shouldLogout = true;
      } else if (errorMsg.includes('네트워크') || 
                 errorMsg.includes('ECONNREFUSED') || 
                 errorMsg.includes('timeout') ||
                 errorMsg.includes('연결')) {
        errorMessage = '네트워크 연결을 확인해주세요. 잠시 후 자동으로 재시도됩니다.';
        shouldLogout = false; // 네트워크 오류는 일시적일 수 있음
      } else if (errorMsg.includes('토큰 갱신 최대 재시도 횟수 초과')) {
        errorMessage = '토큰 갱신에 여러 번 실패했습니다. 다시 로그인해주세요.';
        shouldLogout = true;
      } else if (errorMsg.includes('토큰 갱신 간격이 너무 짧습니다')) {
        // 갱신 간격 문제는 일시적이므로 에러만 표시
        errorMessage = '잠시 후 자동으로 재시도됩니다.';
        shouldLogout = false;
      } else {
        errorMessage = getSimpleErrorMessage(error);
        // 알 수 없는 오류는 로그아웃하지 않음 (일시적 오류일 수 있음)
      }

      setError(errorMessage);

      // 인증 관련 오류인 경우에만 로그아웃 처리
      if (shouldLogout) {
        // 기존 타이머 정리
        if (logoutTimerRef.current) {
          clearTimeout(logoutTimerRef.current);
        }
        logoutTimerRef.current = setTimeout(() => {
          logout();
          logoutTimerRef.current = null;
        }, 3000);
      }

      return false;
    }
  }, [logout, setError]);

  // 인증 상태 초기화 (페이지 로드 시 토큰 확인)
  // AuthStateManager와 동기화하여 경쟁 조건 방지
  const initializeAuth = useCallback(async () => {
    console.log('AuthViewModel.initializeAuth 시작');

    const accessToken = TokenManager.getAccessToken();
    const refreshTokenValue = TokenManager.getRefreshToken();
    const adminInfo = AuthService.getAdminInfo();

    console.log('토큰 상태 확인:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshTokenValue,
      hasAdminInfo: !!adminInfo,
      accessTokenExpired: accessToken ? TokenManager.isTokenExpired(accessToken) : true,
      refreshTokenExpired: refreshTokenValue ? TokenManager.isTokenExpired(refreshTokenValue) : true
    });

    // 1. access token이 유효한 경우 - 갱신 시도하지 않고 바로 로그인 상태로 설정
    // AuthStateManager의 shouldRefreshAccessToken과 동일한 로직 사용
    const isAccessTokenValid = accessToken && !TokenManager.isTokenExpired(accessToken);
    if (isAccessTokenValid) {
      // AuthStateManager와 동기화: 갱신이 필요하지 않으면 상태만 업데이트
      console.log('✅ Access token이 유효함 - 갱신 없이 로그인 상태 유지', {
        accessTokenExpiresIn: accessToken ? TokenManager.getTokenInfo(accessToken)?.expiresIn : 'N/A'
      });
      setLoginSuccess(adminInfo || { admin_id: '', admin_name: '', role: 'admin' });
      return;
    }

    // 2. access token이 만료되었지만 refresh token이 유효한 경우
    // AuthStateManager를 통한 통합 갱신 (경쟁 조건 방지)
    const isRefreshTokenValid = refreshTokenValue && !TokenManager.isTokenExpired(refreshTokenValue);
    if (isRefreshTokenValid) {
      console.log('⚠️ Access token 만료됨, Refresh token이 유효함 - AuthStateManager를 통한 토큰 갱신 시도', {
        accessTokenExpired: !accessToken || TokenManager.isTokenExpired(accessToken),
        refreshTokenExpiresIn: refreshTokenValue ? TokenManager.getTokenInfo(refreshTokenValue)?.expiresIn : 'N/A'
      });
      
      try {
        // AuthStateManager를 통한 통합 갱신 (동시성 제어 포함)
        const newToken = await authStateManager.getValidToken();
        
        if (newToken) {
          console.log('토큰 갱신 성공');
          setLoginSuccess(adminInfo || { admin_id: '', admin_name: '', role: 'admin' });
          return;
        } else {
          // 갱신 실패했지만 refresh token이 여전히 유효하면 로그인 상태 유지
          // (다음 API 호출 시 인터셉터에서 재시도)
          console.warn('토큰 갱신 실패했지만 refresh token이 유효함 - 일시적 오류일 수 있음');
          if (!TokenManager.isTokenExpired(refreshTokenValue)) {
            console.log('Refresh token이 여전히 유효하므로 로그인 상태 유지');
            setLoginSuccess(adminInfo || { admin_id: '', admin_name: '', role: 'admin' });
            return;
          }
        }
      } catch (error: any) {
        console.error('토큰 갱신 중 예외 발생:', error);
        
        // 갱신 간격 문제나 일시적 오류는 로그인 상태 유지
        const errorMsg = error.message || '';
        if (errorMsg.includes('토큰 갱신 간격이 너무 짧습니다') ||
            errorMsg.includes('네트워크') ||
            (!errorMsg.includes('만료') && !errorMsg.includes('없거나'))) {
          console.log('일시적 오류로 판단, 로그인 상태 유지');
          if (!TokenManager.isTokenExpired(refreshTokenValue)) {
            setLoginSuccess(adminInfo || { admin_id: '', admin_name: '', role: 'admin' });
            return;
          }
        }
        // 실제 인증 오류인 경우에만 로그아웃 처리
      }
    }

    // 3. 모든 토큰이 무효한 경우에만 로그아웃
    console.log('모든 토큰이 무효함, 로그아웃 처리');
    setLogoutState();
  }, [setLoginSuccess, setLogoutState]);

  // 에러 메시지 클리어
  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  // 에러 타입에 따른 메시지 변환
  const getErrorMessage = useCallback((errorType: AuthErrorType): string => {
    switch (errorType) {
      case AuthErrorType.INVALID_CREDENTIALS:
        return '아이디 또는 비밀번호가 올바르지 않습니다.';
      case AuthErrorType.NETWORK_ERROR:
        return '네트워크 연결을 확인해주세요.';
      case AuthErrorType.TOKEN_EXPIRED:
        return '세션이 만료되었습니다. 다시 로그인해주세요.';
      case AuthErrorType.REFRESH_FAILED:
        return '인증 갱신에 실패했습니다. 다시 로그인해주세요.';
      default:
        return '알 수 없는 오류가 발생했습니다.';
    }
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리 (메모리 누수 방지)
  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };
  }, []);

  return {
    // 상태
    authState,

    // 액션
    login,
    logout,
    refreshToken,
    initializeAuth,
    validateLoginForm,
    clearError,
    getErrorMessage,

    // 유틸리티
    isLoggedIn: authState.isLoggedIn,
    isLoading: authState.isLoading,
    error: authState.error,
    adminInfo: authState.adminInfo,
  };
};

export default useAuthViewModel; 